import MultipleChoiceOption from 'models/MultipleChoiceOption';
import Question from 'models/Question';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MultipleChoiceOptionItem from './MultipleChoiceOption';

type MultipleChoiceOptionProps = {
  question: Question;
  choices: any[];
  allowImages: boolean;
  allowCorrectChoices?: boolean;
  onModifyQuestionChoice: (
    question: Question,
    choice: MultipleChoiceOption,
    text: string,
    correct: boolean,
  ) => void;
  onDeleteQuestionChoice: (
    question: Question,
    choice: MultipleChoiceOption,
  ) => void;
  onReorderQuestionChoices: (reorderedChoices: MultipleChoiceOption[]) => void;
};

export const MultipleChoiceOptionEditor: React.FC<
  MultipleChoiceOptionProps
> = ({
  question,
  choices,
  allowImages,
  allowCorrectChoices = true,
  onModifyQuestionChoice,
  onDeleteQuestionChoice,
  onReorderQuestionChoices,
}) => {
  const [isDefaultView, setIsDefaultView] = useState<boolean>(true);
  // this component should handle the teacher side for adding/modifying/deleting multiple
  // choice options for AssignmentQuestions for image based answers and test based answers
  useEffect(() => {
    const anyImgs = choices.some((choice) => choice.img || choice.img_file);
    setIsDefaultView(!anyImgs);
  }, [choices, setIsDefaultView]);

  const handleChoiceImageUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    choice: any,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        const imageAsString = e.target?.result;
        const updatedChoice = {
          ...choice,
          img: imageAsString?.toString(),
          img_file: file,
        };

        onModifyQuestionChoice(
          question,
          updatedChoice,
          updatedChoice.text,
          updatedChoice.correct,
        );
      };

      reader.readAsDataURL(file);
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>, toIndex: number) => {
      e.preventDefault();
      const fromIndex = Number(e.dataTransfer.getData('text/plain'));
      if (fromIndex === toIndex) return;

      const newOrder = Array.from(choices);
      const [moved] = newOrder.splice(fromIndex - 1, 1);
      newOrder.splice(toIndex - 1, 0, moved);

      const updated = newOrder.map((c, i) =>
        MultipleChoiceOption.fromServerOption({ ...c, index: i + 1 }),
      );
      onReorderQuestionChoices(updated);
    },
    [choices, onReorderQuestionChoices],
  );

  const editorChoices = useMemo(() => choices, [choices]);

  return (
    <>
      {isDefaultView ? (
        <div className="multiple-choice-options-default">
          {editorChoices
            .sort((c1, c2) => c1.index - c2.index)
            .map((choice) => (
              <MultipleChoiceOptionItem
                key={`multichoiceitem-${choice.index}`}
                question={question}
                choice={choice}
                index={choice.index}
                allowImages={allowImages}
                isDefaultView={isDefaultView}
                allowCorrectChoices={allowCorrectChoices}
                onDrop={handleDrop}
                onModifyQuestionChoice={onModifyQuestionChoice}
                onChoiceImageUpload={handleChoiceImageUpload}
                onDeleteQuestionChoice={onDeleteQuestionChoice}
              />
            ))}
        </div>
      ) : (
        <div className="multiple-choice-options-visual">
          {editorChoices
            .sort((c1, c2) => c1.index - c2.index)
            .map((choice) => (
              <MultipleChoiceOptionItem
                key={`multichoiceitem-${choice.index}`}
                question={question}
                choice={choice}
                index={choice.index}
                allowImages={allowImages}
                isDefaultView={isDefaultView}
                allowCorrectChoices={allowCorrectChoices}
                onDrop={handleDrop}
                onModifyQuestionChoice={onModifyQuestionChoice}
                onChoiceImageUpload={handleChoiceImageUpload}
                onDeleteQuestionChoice={onDeleteQuestionChoice}
              />
            ))}
        </div>
      )}
    </>
  );
};

export default MultipleChoiceOptionEditor;
